import { ref, watch } from "vue";
import {
	createFilterManager,
	BookableRoomsFilter,
	MultiEquipmentFilter,
	MultiRoomTypeFilter,
	SeatCountFilter,
	DimOtherBookingsFilter,
	UsageTypeFilter,
	PersonFilter,
	RoomSearchFilter,
} from "../filters/index.js";
import { useLocalStorageFilterPersistence } from "./useLocalStorageFilterPersistence.js";

/**
 * Composable for managing calendar filters
 */
export function useCalendarFilters(calendarRef = null, masterData = null) {
	const filterManager = createFilterManager();
	const initialized = ref(false);

	// Clear legacy persisted filters once so defaults are restored.
	const { clearFilterPersistence } = useLocalStorageFilterPersistence();
	clearFilterPersistence();

	/**
	 * Initialize filters based on available master data
	 */
	const initializeFilters = async () => {
		if (initialized.value || !masterData) return;

		try {
			// Add bookable rooms filter (always available)
			filterManager.addFilter(new BookableRoomsFilter(false));

			// Add multi-equipment filter (always available)
			const multiEquipmentFilter = new MultiEquipmentFilter(false);
			if (masterData?.equipmentsList?.value) {
				multiEquipmentFilter.setEquipmentOptions(
					masterData.equipmentsList.value
				);
			}
			filterManager.addFilter(multiEquipmentFilter);

			// Add multi-room-type filter (always available)
			const multiRoomTypeFilter = new MultiRoomTypeFilter(false);
			if (masterData?.bookableRoomTypesList?.value) {
				multiRoomTypeFilter.setRoomTypeOptions(
					masterData.bookableRoomTypesList.value
				);
			}
			filterManager.addFilter(multiRoomTypeFilter);

			// Add seat count filter (always available)
			const seatCountFilter = new SeatCountFilter(false);
			filterManager.addFilter(seatCountFilter);

			// Add room search filter (always available)
			const roomSearchFilter = new RoomSearchFilter(false);
			filterManager.addFilter(roomSearchFilter);

			// Add dim other bookings filter (default active, only for students/employees)
			const userRole = masterData?.userRole?.value;
			if (userRole === "student" || userRole === "employee") {
				filterManager.addFilter(new DimOtherBookingsFilter(true));
			}

			const canViewOtherBookings =
				masterData?.canViewOtherBookings?.value || false;

			// Add booking filters (only for people who can manage other bookings)
			if (canViewOtherBookings) {
				// Add usage type filter
				const usageTypeFilter = new UsageTypeFilter(false);
				if (masterData?.bookingTypesList?.value) {
					usageTypeFilter.setUsageTypeOptions(
						masterData.bookingTypesList.value
					);
				}
				filterManager.addFilter(usageTypeFilter);

				// Add person filter
				const personFilter = new PersonFilter(false);
				filterManager.addFilter(personFilter);
			}

		initialized.value = true;
		} catch (error) {
			console.error("Failed to initialize calendar filters:", error);
		}
	};

	/**
	 * Apply room filters to resources
	 */
	const applyRoomFilters = (resources, context = {}) => {
		return filterManager.applyFilters(resources, "room", context);
	};

	/**
	 * Apply booking filters to events/bookings
	 */
	const applyBookingFilters = (bookings, context = {}) => {
		return filterManager.applyFilters(bookings, "booking", context);
	};

	/**
	 * Get room IDs that have bookings matching active booking filters
	 */
	const getRoomIdsWithFilteredBookings = (bookings) => {
		return filterManager.getRoomIdsWithFilteredBookings(bookings);
	};

	/**
	 * Setup calendar refresh when filters change
	 */
	const setupCalendarRefresh = (saveExpansionStateCallback = null, options = {}) => {
		if (!calendarRef) return;

		const { refreshResources = true } = options;

		const unsubscribe = filterManager.onChange(() => {
			if (calendarRef.value) {
				try {
					const calendarApi = calendarRef.value.getApi();
					
					// Save expansion state before refetching resources
					if (refreshResources && saveExpansionStateCallback) {
						saveExpansionStateCallback(calendarApi);
					}
					
					if (refreshResources) {
						// Refetch resources for room filters
						calendarApi.refetchResources();
					}
					// Refetch events for booking filters
					calendarApi.refetchEvents();
				} catch (error) {
					console.error("Error refreshing calendar with filters:", error);
				}
			}
		});

		return unsubscribe;
	};

	/**
	 * Get filter configurations for UI rendering
	 */
	const getRoomFilterConfigs = () => {
		// Get both room and UI filters for display
		const roomConfigs = filterManager.getFilterConfigs("room");
		const uiConfigs = filterManager.getFilterConfigs("ui");
		const configs = [...roomConfigs, ...uiConfigs];
		return configs;
	};

	const getDimOtherBookingsFilterConfigs = () => {
		const dimFilterConfig = filterManager
			.getFilterConfigs("ui")
			.find((filter) => filter.id === "dim-other-bookings");

		return dimFilterConfig ? [dimFilterConfig] : [];
	};

	/**
	 * Get booking filter configurations for UI rendering
	 */
	const getBookingFilterConfigs = () => {
		return filterManager.getFilterConfigs("booking");
	};

	/**
	 * Toggle a filter by ID
	 */
	const toggleFilter = (filterId) => {
		return filterManager.toggleFilter(filterId);
	};

	/**
	 * Get active filter count
	 */
	const getActiveFilterCount = () => {
		return filterManager.activeFilterCount.value;
	};

	/**
	 * Clear all active filters
	 */
	const clearAllFilters = () => {
		filterManager.clearAllFilters();
	};

	/**
	 * Update equipment selection in the multi-equipment filter
	 */
	const updateEquipmentSelection = (selectedEquipmentIds) => {
		const multiEquipmentFilter = filterManager.getFilter("multi-equipment");
		if (multiEquipmentFilter) {
			// Clear current selection
			multiEquipmentFilter.clearSelection();

			// Add each selected equipment
			selectedEquipmentIds.forEach((id) => {
				multiEquipmentFilter.addEquipment(id);
			});

			// Update equipment options if they changed
			if (masterData?.equipmentsList?.value) {
				multiEquipmentFilter.setEquipmentOptions(
					masterData.equipmentsList.value
				);
			}

			// Manually trigger change notification since we're modifying the filter directly
			filterManager._notifyChange();
		}
	};

	/**
	 * Update room type selection in the multi-room-type filter
	 */
	const updateRoomTypeSelection = (selectedRoomTypeIds) => {
		const multiRoomTypeFilter = filterManager.getFilter("multi-room-types");
		if (multiRoomTypeFilter) {
			// Clear current selection
			multiRoomTypeFilter.clearSelection();

			// Add each selected room type
			selectedRoomTypeIds.forEach((id) => {
				multiRoomTypeFilter.addRoomType(id);
			});

			// Update room type options if they changed
			if (masterData?.bookableRoomTypesList?.value) {
				multiRoomTypeFilter.setRoomTypeOptions(
					masterData.bookableRoomTypesList.value
				);
			}

			// Manually trigger change notification since we're modifying the filter directly
			filterManager._notifyChange();
		}
	};

	/**
	 * Update minimum seat count in the seat count filter
	 */
	const updateSeatCountFilter = (minSeats) => {
		const seatCountFilter = filterManager.getFilter("seat-count");
		if (seatCountFilter) {
			seatCountFilter.setMinSeats(minSeats);
			// Manually trigger change notification since we're modifying the filter directly
			filterManager._notifyChange();
		}
	};

	/**
	 * Get available equipment options from master data
	 */
	const getAvailableEquipment = () => {
		if (!masterData?.equipmentsList?.value) return [];
		return masterData.equipmentsList.value;
	};

	/**
	 * Get available room type options from master data
	 */
	const getAvailableRoomTypes = () => {
		if (!masterData?.bookableRoomTypesList?.value) return [];
		return masterData.bookableRoomTypesList.value;
	};

	/**
	 * Check if dim other bookings filter is active
	 */
	const isDimOtherBookingsActive = () => {
		const filter = filterManager.getFilter("dim-other-bookings");
		return filter?.active || false;
	};

	/**
	 * Update usage type selection in the usage type filter
	 */
	const updateUsageTypeSelection = (usageTypeId) => {
		const usageTypeFilter = filterManager.getFilter("usage-type");
		if (usageTypeFilter) {
			usageTypeFilter.setUsageType(usageTypeId);

			// Update usage type options if they changed
			if (masterData?.bookingTypesList?.value) {
				usageTypeFilter.setUsageTypeOptions(masterData.bookingTypesList.value);
			}

			// Manually trigger change notification
			filterManager._notifyChange();
		}
	};

	/**
	 * Update person selection in the person filter
	 */
	const updatePersonSelection = (personId, personName) => {
		const personFilter = filterManager.getFilter("person-filter");
		if (personFilter) {
			if (personId && personName) {
				// Set new person selection (this will clear any previous selection first)
				personFilter.setSelectedPerson(personId, personName);
			} else {
				// Clear selection completely
				personFilter.clearSelection();
			}
			// Manually trigger change notification
			filterManager._notifyChange();
		}
	};

	/**
	 * Update person search query
	 */
	const updatePersonSearchQuery = (query) => {
		const personFilter = filterManager.getFilter("person-filter");
		if (personFilter) {
			personFilter.setSearchQuery(query);
		}
	};

	/**
	 * Get available usage types from master data
	 */
	const getAvailableUsageTypes = () => {
		if (!masterData?.bookingTypesList?.value) return [];
		return masterData.bookingTypesList.value;
	};

	/**
	 * Update room search query
	 */
	const updateRoomSearch = (query) => {
		const roomSearchFilter = filterManager.getFilter("room-search");
		if (roomSearchFilter) {
			roomSearchFilter.setSearchQuery(query);
			// Manually trigger change notification
			filterManager._notifyChange();
		}
	};

	/**
	 * Get current room search query
	 */
	const getRoomSearchQuery = () => {
		const roomSearchFilter = filterManager.getFilter("room-search");
		return roomSearchFilter?.getSearchQuery() || "";
	};

	// Auto-initialize when master data is available
	if (masterData) {
		// Watch for master data loading state
		watch(
			() => masterData.isLoaded?.value,
			(isLoaded) => {
				if (isLoaded && !initialized.value) {
					initializeFilters();
				}
			},
			{ immediate: true }
		);
	}

	return {
		filterManager,
		initializeFilters,
		applyRoomFilters,
		applyBookingFilters,
		getRoomIdsWithFilteredBookings,
		setupCalendarRefresh,
		getRoomFilterConfigs,
		getDimOtherBookingsFilterConfigs,
		getBookingFilterConfigs,
		toggleFilter,
		getActiveFilterCount,
		clearAllFilters,
		updateEquipmentSelection,
		updateRoomTypeSelection,
		updateSeatCountFilter,
		getAvailableEquipment,
		getAvailableRoomTypes,
		isDimOtherBookingsActive,
		updateUsageTypeSelection,
		updatePersonSelection,
		updatePersonSearchQuery,
		getAvailableUsageTypes,
		updateRoomSearch,
		getRoomSearchQuery,
		initialized,
	};
}
